import Image from "next/image";
import styled, { css } from "styled-components";

import { Button } from "components/atoms/Button";
import { device } from "utils/sizes";
import { colourScheme } from "utils/styling/colours";
import {
    Label as TypoLabel,
    Title as StyledTitle,
} from "utils/styling/typography.styled";

export const Main = styled.main`
    gap: 2rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    align-self: center;
    width: 100%;
    ${device.lg} {
        // width: 80%;
    }
`;

export const ImageSection = styled.div`
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    padding: 0;

    ${device.lg} {
        max-width: 50%;
        flex-shrink: 0;
    }
`;

export const VideoThumbnail = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100px;
    height: 100px;
    background-color: #f0f0f0;
    cursor: pointer;
`;

export const ImageWrapper = styled.div<{ $aspectRatio: number }>`
    position: relative;
    overflow: hidden;
    width: 100%;
    border-radius: 0.5rem;
    padding-top: ${(props) => (1 / props.$aspectRatio) * 100}%;
    /* Fallback for browsers that support aspect-ratio */
    @supports (aspect-ratio: auto) {
        padding-top: 0;
        aspect-ratio: ${(props) => props.$aspectRatio};
    }
`;

export const Title = styled(StyledTitle)`
    text-align: center;
    margin-top: 1rem;
    font-size: 2rem;
`;

export const StyledModal = styled.div`
    display: flex;
    flex-direction: column;
    ${device.lg} {
        flex-direction: row;
    }
    justify-content: space-between;
    // overflow-y: auto;
    gap: 1rem;
    max-width: 90vw;
`;

export const ImageContainer = styled.div`
    flex: 3;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    border-radius: 0.5rem;
    padding: 1rem;
`;

export const StyledImage = styled(Image)`
    border-radius: 0.5rem;
    max-height: 75vh;
`;

export const ControlsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    justify-content: center;
    gap: 1rem;
    padding: 1rem 0rem;
    align-items: center;
    width: 100%;
    @media (max-width: ${device.md}) {
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;
    }
`;

export const ToggleContainer = styled.div`
    display: flex;
    gap: 0.5rem;
`;
export const Icon = styled.div`
    display: inline-block;
    cursor: pointer;
    position: relative;
    width: 1.5rem;
    height: 1.5rem;
`;

export const HeartCheckbox = styled.input.attrs({ type: "checkbox" })`
    opacity: 0;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    cursor: pointer;
`;

export const IconWrapper = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 1.5rem;
    height: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s ease-in-out;

    &:hover {
        transform: scale(1.1);
    }
`;

export const ReactContainer = styled.div`
    display: flex;
    gap: 0.5rem;
`;

export const Label = styled(TypoLabel)`
    display: inline-flex;
    align-items: center;
    font-size: 1rem;
`;

export const HorizontalRule = styled.hr`
    border: none;
    height: 0.05rem;
    background-color: ${colourScheme.fullWhite};
    margin: 1rem 0;
    width: 100%;
`;

export const ChildImagesSection = styled.div`
    margin-top: 1.5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
`;

export const SectionTitle = styled.h2`
    font-size: 1.25rem;
    margin-bottom: 1rem;
    color: ${colourScheme.white};
    text-align: center;
`;

export const GalleryContainer = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;

    .image-gallery-thumbnail {
        width: auto;
        height: 60px; /* Adjust this value to set the thumbnail height */
    }

    .image-gallery-slide img {
        border-radius: 0.2rem;
    }
`;

export const CarouselContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    margin-top: 1rem;
    overflow-x: auto;
    padding: 0.5rem;
    scroll-behavior: smooth;
`;

export const CarouselItem = styled.div`
    cursor: pointer;
    margin: 0 0.5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

export const ThumbnailImage = styled(Image)<{ $isSelected: boolean }>`
    border-radius: 0.5rem;
    object-fit: cover;
    transition: transform 0.2s ease-in-out;
    border: 3px solid transparent;
    border-radius: 0.5rem;
    flex-shrink: 0;

    ${({ $isSelected }) =>
        $isSelected &&
        css`
            border-color: ${colourScheme.secondary};
        `}
    &:hover {
        transform: scale(1.1);
    }
`;

export const ViewImageButton = styled(Button)`
    margin-top: 0.5rem;
    padding: 0.25rem;
    font-size: 1rem;
    color: ${colourScheme.white};
    background-color: transparent;
    border: none;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background-color: ${colourScheme.primaryLight};
        color: ${colourScheme.fullWhite};
    }

    &:focus {
        outline: none;
    }
`;

export const MetadataSection = styled.div`
    overflow-y: hidden;
    flex-shrink: 0;
    ${device.lg} {
        max-width: 50%;
        overflow-y: auto;
        flex: 1;
    }
`;
